import React from 'react';
import { Menu } from 'antd';
import { Link, useLocation } from 'react-router-dom';
import { useReactiveVar } from '@apollo/client';
import { currentUserVar } from '@/graphql/operations/user';
import { EmployeePermissions, ResourceUIType } from '@/service/Api/types';
import { getPeopleMenuPath } from '@/utils/getPeopleMenuPath';
import styles from './Navbar.module.pcss';

export const getAdminLayoutMenuItems = (peoplePath: '/users' | '/employees' = '/users') => [
  {
    label: 'Earnings',
    key: ResourceUIType.Earnings,
    path: '/earnings',
  },
  {
    label: 'Payroll Assistant',
    key: ResourceUIType.Plans,
    path: '/plans',
  },
  {
    label: 'Scheduling Agent',
    key: ResourceUIType.Events,
    path: '/events',
  },
  {
    label: 'Compliance Agent',
    key: ResourceUIType.Compliance,
    path: '/compliance',
  },
  {
    label: 'People',
    key: ResourceUIType.Users,
    path: peoplePath,
  },
  {
    label: 'Insights',
    key: ResourceUIType.Insights,
    path: 'insights',
    children: [
      {
        label: 'Insights',
        key: ResourceUIType.Insights,
        path: '/insights',
      },
      {
        label: 'Performance',
        key: ResourceUIType.Performance,
        path: '/performance',
      },
    ],
  },
  {
    label: 'Notifications',
    key: ResourceUIType.Notifications,
    path: '/notifications',
  },
  {
    label: 'Integrations',
    key: ResourceUIType.Connectors,
    path: 'integrations',
    children: [
      {
        label: 'Connectors',
        key: ResourceUIType.Connectors,
        path: '/connectors',
      },
      {
        label: 'Widgets',
        key: ResourceUIType.Widgets,
        path: '/widgets',
      },
    ],
  },
];

export const adminLayoutMenuItems = getAdminLayoutMenuItems();

const filterMenus = (menus: typeof adminLayoutMenuItems, permissions?: EmployeePermissions[]) => {
  return menus.filter((menu) => {
    const permission = permissions?.find((p) => p.resource === menu.key);

    if (permission && permission.can.includes('read')) {
      return true;
    }

    if (
      menu.children &&
      menu.children.length > 0 &&
      menu.children.some((child) => child.key === permission?.resource)
    ) {
      menu.children = filterMenus(menu.children, permissions);
      return true;
    }

    return false;
  });
};

const Navbar = () => {
  const location = useLocation();
  const currentUser = useReactiveVar(currentUserVar);

  const peopleMenuPath = getPeopleMenuPath(currentUser);
  const adminMenuItems = getAdminLayoutMenuItems(peopleMenuPath);

  const getSelectedMenuKey = (pathname: string) => {
    if (pathname === '/employees' || pathname.startsWith('/employees/')) {
      return peopleMenuPath;
    }

    if (pathname === '/users' || pathname.startsWith('/users/')) {
      return peopleMenuPath;
    }

    return pathname;
  };

  const getMenuItemPath: any = (item: (typeof navMenuItems)[0]) => {
    return {
      key: item.path,
      label: (
        <Link
          to={item.children ? '#' : `${item.path}`}
          replace={item.path === location.pathname}
          style={getSelectedMenuKey(location.pathname) === item.path ? { color: '#ffffff' } : {}}
        >
          {item.label}
        </Link>
      ),
      children: item.children?.map((child) => getMenuItemPath(child)),
      className: styles.menuItem,
    };
  };

  const filteredMenus = adminMenuItems.filter((item) => {
    if (process.env.ARYA_PLATFORM === 'production' && !currentUser.organization.config.enableScheduling) {
      return item.key !== 'events';
    }
    return item;
  });

  const navMenuItems = filterMenus(filteredMenus, currentUser.permissions?.layoutAccess);

  const getNavMenuItems = () => {
    return navMenuItems.map((item) => getMenuItemPath(item));
  };

  return (
    <Menu
      className={styles.root}
      theme="light"
      mode="horizontal"
      selectedKeys={[getSelectedMenuKey(location.pathname)]}
      items={getNavMenuItems()}
    />
  );
};

export default Navbar;
